import { Box, CircularProgress, Typography } from "@mui/material"
import { SearchOutlined } from "@mui-symbols-material/w300"
import React, { useCallback, useMemo, useState } from "react"
import { useFormContext } from "react-hook-form"

import { AccountManagerCheckbox } from "./AccountManagerCheckbox"

import { InputField } from "@/components/common/InputField"
import type { CompanyListFilter } from "@/graphql/codegen/graphql"
import { useAccountManagers } from "@/hooks/api/useAccountManagers"

interface AccountManagerFilterProps {
  onFilterChange: (name: keyof CompanyListFilter, value: any) => void
}

export const AccountManagerFilter = React.memo<AccountManagerFilterProps>(({ onFilterChange }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const { control, watch } = useFormContext()
  const accountManagersValue = watch("accountManagers") || []

  const { visibleManagers, fetching } = useAccountManagers({})

  const filteredManagers = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase()
    const seen = new Set<string>()

    return visibleManagers.filter((manager) => {
      const fullName = manager.name.toLocaleLowerCase()

      if (seen.has(manager.userId)) {
        return false
      }

      const isMatch = fullName.includes(normalizedSearchTerm)

      if (isMatch) {
        seen.add(manager.userId)
        return true
      }

      return false
    })
  }, [searchTerm, visibleManagers])

  const handleFilterChange = useCallback(
    (newValue: string[]) => {
      onFilterChange("accountManagers", newValue) // Update the selected accountManager IDs in the filter
    },
    [onFilterChange]
  )

  return (
    <Box display='flex' flexDirection='column' height='100%'>
      <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
        Account Manager
      </Typography>

      <Box className='flex-none p-4'>
        <InputField
          placeholder='Search by Name'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          startAdornment={<SearchOutlined className='text-gray-600' />}
          endAdornment={fetching && <CircularProgress size={20} />}
        />
      </Box>

      <Box className='flex grow flex-col overflow-hidden'>
        <Box className='grow overflow-y-auto px-8 py-2'>
          {filteredManagers.map((manager) => (
            <AccountManagerCheckbox
              key={manager.userId}
              manager={manager}
              control={control}
              name='accountManagers'
              accountManagersValue={accountManagersValue}
              onFilterChange={handleFilterChange}
            />
          ))}
          {fetching && (
            <Box display='flex' justifyContent='center' py={2}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
})
